import type { AlphaMark } from "@nlc/shared-types";

import type { EditorTool } from "./editorTools";

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;

export type EditorMark = Extract<AlphaMark, { type: EditorTool }>;

export type NewMark = DistributiveOmit<EditorMark, "instruction" | "n">;

export type MarksHistory = {
  future: EditorMark[][];
  marks: EditorMark[];
  past: EditorMark[][];
};

export type MarksAction =
  | { instruction: string; n: number; type: "setInstruction" }
  | { mark: NewMark; type: "add" }
  | { n: number; type: "remove" }
  | { type: "redo" | "undo" };

export const initialMarksHistory: MarksHistory = {
  future: [],
  marks: [],
  past: [],
};

function commit(state: MarksHistory, marks: EditorMark[]): MarksHistory {
  return { future: [], marks, past: [...state.past, state.marks] };
}

export function marksReducer(
  state: MarksHistory,
  action: MarksAction,
): MarksHistory {
  switch (action.type) {
    case "add": {
      return commit(state, [
        ...state.marks,
        { ...action.mark, instruction: "", n: state.marks.length + 1 },
      ] as EditorMark[]);
    }
    case "redo": {
      const next = state.future.at(-1);

      return next
        ? {
            future: state.future.slice(0, -1),
            marks: next,
            past: [...state.past, state.marks],
          }
        : state;
    }
    case "remove": {
      return commit(
        state,
        state.marks
          .filter(({ n }) => n !== action.n)
          .map((mark, index) => ({ ...mark, n: index + 1 })),
      );
    }
    case "setInstruction": {
      return {
        ...state,
        marks: state.marks.map((mark) =>
          mark.n === action.n
            ? { ...mark, instruction: action.instruction }
            : mark,
        ),
      };
    }
    case "undo": {
      const previous = state.past.at(-1);

      return previous
        ? {
            future: [...state.future, state.marks],
            marks: previous,
            past: state.past.slice(0, -1),
          }
        : state;
    }
  }
}
